/* eslint-disable max-classes-per-file */
import type { BookingFlightAddonEnum } from "@/app/[lang]/flights/booking/components/BookingFlightsLayoutComponent/BookingFlightEnum";
import { AddonTabEnum } from "@/utils/enums/FlightAddonEnum";

import { ResponsePresenter } from "../../common/commonClassPresenter";
import { CorporateDisCountPresenter } from "../../corporate/corporateServiceInterfaces/corporateInterface";
import {
  BaggageFee,
  MealFeeList,
  Package,
  Segment,
  SportEquipmentFeesList,
} from "./getFlightFaresInterface";

export interface PassengerSelectedInsuranceInterface {
  selected: number;
  passengerKey: string;
}

export interface DepartureInterface {
  searchPriceId: string;
  supplierCode: string;
  supplierName: string;
  customerAgreements: {
    termAndConditionsTitle: string;
    termAndConditions: string;
    fareRules: string;
  };
  packages: Package;
  baggageFeesList: FlightBookingAdditionalBaggageFeesList[];
  sportEquipmentFeesList: SportEquipmentFeesList[];
  mealFeesList: MealFeesList[];
  segments: Segment[];
}

export interface BaggageTrackingService {
  productcode: string;
  wholesale: string;
  priceUSD: number;
  price: number;
  currency: string;
  benefit: string;
  benefitUSD: string;
  benefitTHB: string;
  serviceDetails: string;
}

export interface ReturnInterface {
  searchPriceId: string;
  supplierCode: string;
  supplierName: string;
  customerAgreements: {
    termAndConditionsTitle: string;
    termAndConditions: string;
    fareRules: string;
  };
  packages: Package;
  sportEquipmentFeesList: SportEquipmentFeesList[];
  baggageFeesList: {
    baggageConcept: string;
    baggageKey: string;
    baggageType: string;
    baggageName: string;
    baggagePrice: number;
    baggagePiece: number;
    baggageUnit: string;
    baggageTotalWeight: number;
    baggageCurrency: string;
    baggageIcon: string;
    baggageLeg: string;
  }[];

  mealFeesList: MealFeesList[];

  segments: Segment[];
}

interface FlightDetailPrice {
  amount: number;
  fare: number;
  tax: number;
}
export class FlightFarePriceType {
  amount: number;

  fare: number;

  increase: number;

  newPrice: FlightDetailPrice;

  oldPrice: FlightDetailPrice;

  passengerAmount: number;

  tax: number;

  constructor(data: any) {
    this.amount = data?.amount || 0;

    this.fare = data?.fare || 0;

    this.increase = data?.increase || 0;

    this.newPrice = data?.newPrice;

    this.oldPrice = data?.oldPrice;
    this.passengerAmount = data?.passengerAmount || 0;
    this.tax = data?.tax || 0;
  }
}
export class FlightFarePriceLists {
  adult: FlightFarePriceType;

  child: FlightFarePriceType;

  infant: FlightFarePriceType;

  amount: number;

  amountOld: number;

  constructor(data: any) {
    this.adult = new FlightFarePriceType(data?.adult);
    this.child = new FlightFarePriceType(data?.child);
    this.infant = new FlightFarePriceType(data?.infant);
    this.amount = data?.amount || 0;
    this.amountOld = data?.amountOld || 0;
  }
}

export class FlightPriceLists {
  amount: number;

  currency: string;

  fare: FlightFarePriceLists;

  increase: number;

  paymentFee: number;

  priceChangeType?: "in" | "out";

  constructor(data: any) {
    this.amount = data?.amount || 0;
    this.currency = data?.currency || "";
    this.fare = new FlightFarePriceLists(data?.fare);
    this.increase = data?.increase || 0;
    this.paymentFee = data?.paymentFee || 0;
    this.priceChangeType = data?.priceChangeType || "out";
  }
}
export class SearchFlightFareDetailInterface extends CorporateDisCountPresenter {
  departureFare: DepartureInterface;

  returnFare?: ReturnInterface;

  passengers: Passengers;

  pricePerPassenger: PricePerPassenger[];

  priceIncrease: number;

  isPriceIncrease: boolean;

  sellingPrice: number;

  currency: string;

  baggageTrackingService: BaggageTrackingService;

  airlineMembers: AirlineMembers;

  typeOfFares: string;

  typeOfFlight: string;

  keySearchTimeExpire: number;

  paymentFee: number;

  priceLists: FlightPriceLists;

  constructor(data: any) {
    super(data);
    // NOTE - check if baggage piece is more than 1 for departure leg
    const baggageFeesListD: any[] = data.departure?.BaggageFeesList || [];
    const isSomeBagMoreThanOneD = baggageFeesListD.some(
      (bag) => (bag?.BaggagePiece || 0) > 1,
    );
    // NOTE - check if baggage piece is more than 1 for return leg
    const baggageFeesListR: any[] = data.return?.BaggageFeesList || [];
    const isSomeBagMoreThanOneR = baggageFeesListR.some(
      (bag) => (bag?.BaggagePiece || 0) > 1,
    );

    this.departureFare = {
      searchPriceId: data.departure?.searchPriceId || "",
      supplierCode: data.departure?.supplierCode || "",
      supplierName: data.departure?.supplierName || "",
      packages: new Package(data.departure?.Packages || {}),
      baggageFeesList:
        baggageFeesListD?.map(
          (baggage: BaggageFee) =>
            new BaggageFee(baggage, isSomeBagMoreThanOneD),
        ) || [],
      mealFeesList:
        data.departure?.MealFeesList?.map(
          (meal: MealFeeList) => new MealFeeList(meal),
        ) || [],
      segments:
        data.departure?.Segments?.map(
          (segment: Segment) => new Segment(segment),
        ) || [],

      sportEquipmentFeesList: (
        data.departure?.SportEquipmentFeesList || []
      ).map(
        (item: SportEquipmentFeesList) =>
          new SportEquipmentFeesList({
            BaggageLeg: AddonTabEnum.DEPARTURE,
            ...item,
          }),
      ),

      customerAgreements: {
        termAndConditionsTitle:
          data.departure?.CustomerAgreements?.supplierName || "",
        termAndConditions:
          data.departure?.TermAndConditions?.TermAndConditions || "",
        fareRules: data.departure?.TermAndConditions?.fareRules || "",
      },
    };

    this.priceLists = new FlightPriceLists(data?.PriceLists);

    this.returnFare = data.return
      ? {
          searchPriceId: data.return?.searchPriceId || "",
          supplierCode: data.return?.supplierCode || "",
          supplierName: data.return?.supplierName || "",
          packages: new Package(data.return?.Packages || {}),
          baggageFeesList:
            baggageFeesListR?.map(
              (baggage: BaggageFee) =>
                new BaggageFee(baggage, isSomeBagMoreThanOneR),
            ) || [],

          mealFeesList:
            data.return?.MealFeesList?.map(
              (meal: MealFeeList) => new MealFeeList(meal),
            ) || [],

          sportEquipmentFeesList: (
            data.return?.SportEquipmentFeesList || []
          ).map(
            (item: SportEquipmentFeesList) =>
              new SportEquipmentFeesList({
                BaggageLeg: AddonTabEnum.RETURN,
                ...item,
              }),
          ),

          segments: data.return?.Segments?.map(
            (segment: Segment) => new Segment(segment),
          ) || [new Segment({})],
          customerAgreements: {
            termAndConditionsTitle:
              data.return?.CustomerAgreements?.supplierName || "",
            termAndConditions:
              data.return?.TermAndConditions?.TermAndConditions || "",
            fareRules: data.return?.TermAndConditions?.FareRules || "",
          },
        }
      : undefined;

    this.priceIncrease = data.priceIncrease;

    this.isPriceIncrease = data.isPriceIncrease;

    this.passengers = {
      adult: data.Passengers?.adult || 0,
      child: data.Passengers?.child || 0,
      infant: data.Passengers?.infant || 0,
    };

    this.pricePerPassenger = (data.PricePerPassenger || []).map(
      (item: any) => ({
        amount: item.amount || 0,
        type: item.type || "",
        currency: item.currency || "",
        priceChange: item?.priceChange || 0,
        priceChangeVat: item?.priceChangeVat || 0,
        priceChangeWithoutVat: item?.priceChangeWithoutVat || 0,
        priceWithoutVat: item?.priceWithoutVat || 0,
        vat: item?.vat || 0,
      }),
    );

    this.sellingPrice = data.SellingPrice || 0;
    this.currency = data.Currency || "";

    this.baggageTrackingService = {
      productcode: data.BaggageTrackingService?.productcode || "",
      wholesale: data.BaggageTrackingService?.wholesale || "",
      priceUSD: data.BaggageTrackingService?.priceUSD || 0,
      price: data.BaggageTrackingService?.price || 0,
      currency: data.BaggageTrackingService?.currency || "",
      benefit: data.BaggageTrackingService?.benefit || "",
      benefitUSD: data.BaggageTrackingService?.benefitUSD || "",
      benefitTHB: data.BaggageTrackingService?.benefitTHB || "",
      serviceDetails: data.BaggageTrackingService?.ServiceDetails || "",
    };

    this.airlineMembers = {
      status: data.AirlineMembers?.status || false,
      alliances: data.AirlineMembers?.alliances || [],
    };

    this.typeOfFares = data.TypeOfFares || "";
    this.typeOfFlight = data.TypeOfFlight || "";
    this.keySearchTimeExpire = data.keySearchTimeExpire || 0;
    this.paymentFee = data.PaymentFee;
  }
}

export class ResponseSearchFlightFareDetailInterface {
  code: string;

  data: SearchFlightFareDetailInterface;

  message: string;

  status: boolean;

  constructor(data: any) {
    this.code = data.code;
    this.data = new SearchFlightFareDetailInterface(data.data);
    this.message = data.message;
    this.status = data.status;
  }
}
export interface FlightFaresBaggageFees {
  baggageKey: string;

  baggageConcept: string;

  baggageType: string;

  baggageName: string;

  baggagePrice: number;

  baggagePiece: number;

  baggageUnit: number;

  baggageTotalWeight: number;

  baggageCurrency: string;
}
export interface Packages {
  name: string;
  fareBase: string;
  numberOfStops: string;
  seatAvailable: string;
  freeMeal: string;
  freeMealDesc: string;
  seatSelection: string;
  flexiblePolicy: FlexiblePolicy;
  deviceStreaming: string;
  wifi: string;
  powerOrUSBOutlets: string;
  refundPolicy: RefundPolicy;
  iGoPack: string;
  recordSet: string;
  lowCostAirline: string;
  adultOriginalPrice: number;
  adultPrice: number;
  adultFare: number;
  adultTax: number;
  adultPriceNoCom: number;
  adultFareNoCom: number;
  adultTaxNoCom: number;
  childOriginalPrice: number;
  childPrice: number;
  childFare: number;
  childTax: number;
  childPriceNoCom: number;
  childFareNoCom: number;
  childTaxNoCom: number;
  infantOriginalPrice: number;
  infantPrice: number;
  infantFare: number;
  infantTax: number;
  infantPriceNoCom: number;
  infantFareNoCom: number;
  infantTaxNoCom: number;
  bookingClass: string;
  equivalentBookingClass: string;
  baggageFees: BaggageFee[];
  iGoPackKeys: any[];
  key: string;
}

export interface FlexiblePolicy {
  flexible: string;
  price: number;
  chargeType: string;
  maxChange: string;
  desc: string;
}

export interface RefundPolicy {
  refundable: string;
  refundFee: number;
  refundDesc: string;
}

export interface FlexiblePolicy2 {
  flexible: string;
  price: number;
  chargeType: string;
  maxChange: string;
  desc: string;
}

export interface RefundPolicy2 {
  refundable: string;
  refundFee: number;
  refundDesc: string;
}
export interface Passengers {
  adult: number;
  child: number;
  infant: number;
}
export class PassengersClass {
  adult: number;

  child: number;

  infant: number;

  constructor(res: any) {
    this.adult = res?.adult || 0;
    this.child = res?.child || 0;
    this.infant = res?.infant || 0;
  }
}

export interface PricePerPassenger {
  amount: number;
  type: string;
  currency: string;
  priceChange: number;
  priceChangeVat: number;
  priceChangeWithoutVat: number;
  priceWithoutVat: number;
  vat: number;
}

export interface AirlineMembers {
  status: boolean;
  alliances: any[];
}

export interface SegmentInterface {
  Departure: string;
  DepartureDateTime: string;
  Arrival: string;
  ArrivalDateTime: string;
  Duration: string;
  Operator: string;
  MajorCarrierCode: string;
  CarrierCode: string;
  codeShare: string;
  EquipmentCode: string;
  EquipmentName: string;
  CabinClass: string;
  CabinClassName: string;
  FlightTravelTime: number;
  TravelTime: string;
  Mileage: number;
  TechStop: string;
  TechStopLocation: string;
  TechStopArrivalDateTime: string;
  TechStopDepartureDateTime: string;
  Thumbnail: string;
  DepartureAirportName: string;
  DepartureAirportCityName: string;
  ArrivalAirportName: string;
  ArrivalAirportCityName: string;
  CarrierName: string;
  CarrierNameEn: string;
  TechStopAirportName: string;
  TechStopAirportCityName: string;
  FlightNumber: string;
}

export interface MealItem {
  mealKey: string;
  SSRCode: string;
  name: string;
  nameEn: string;
  price: number;
  imageURL: string;
  flightNumber: string;
}

export interface MealFeesListDeparture {
  mealDetail: MealDetailDeparture;
}

export interface MealFeesList {
  mealDetail: MealDetailDeparture;
}
export interface MealDetailDeparture {
  seatMapLeg: string;
  departureAirport: string;
  arrivalAirport: string;
  departureDateTime: string;
  marketingAirline: string;
  flightNumber: string;
  meals: MealItem[];
}

export interface FlightBookingAdditionalBaggageFeesList {
  baggageConcept: string;
  baggageKey: string;
  baggageType: string;
  baggageName: string;
  baggagePrice: number;
  baggagePiece: number;
  baggageUnit: string;
  baggageLeg: string;
  baggageTotalWeight: number;
  baggageCurrency: string;
  baggageIcon: string;
}
export interface FlightPassengerSelectAdditionalItems {
  key: string;
  leg?: AddonTabEnum;
  segment?: number;
  price?: number;
}
export interface FlightPassengerSelectAdditional {
  [BookingFlightAddonEnum.BAGGAGE]?: FlightPassengerSelectAdditionalItems[];
  [BookingFlightAddonEnum.MEAL]?: FlightPassengerSelectAdditionalItems[];
  [BookingFlightAddonEnum.SEAT]?: FlightPassengerSelectAdditionalItems[];
  [BookingFlightAddonEnum.INSURANCE]?: FlightPassengerSelectAdditionalItems[];
  [BookingFlightAddonEnum.BAGGAGETRACKING]?: FlightPassengerSelectAdditionalItems[];
  [BookingFlightAddonEnum.SPORT_EQUIPMENT]?: FlightPassengerSelectAdditionalItems[];
}

export interface PassengerAndSegmentActive {
  passenger: number;
  segment: number;
}
export interface DeleteButtonBaggageStatus {
  leg: AddonTabEnum;
  status: boolean;
  action: boolean;
  isUpdate: boolean;
}

export interface SearchBaggageDataSelectedInterface {
  baggageFeesList: FlightBookingAdditionalBaggageFeesList[];
  baggageFees: BaggageFee[];
  key: string;
  unit: string;
  leg?: AddonTabEnum;
  pieceText: string;
  isHidePiece?: boolean;
  isPerPiece?: boolean;
}

export interface FilterItemBaggageAndMealInterface {
  name: string;
  price: number;
  namePiece?: string;
  piecePrice?: number;
}

export class RouteFlightClass {
  leg: string;

  currency: string;

  departure: string;

  arrival: string;

  isRecommended: boolean;

  isSpecialDeal: boolean;

  flightTotalTime: number;

  flightTotalDuration: string;

  flightTotalStop: number;

  timeUnit: string;

  isDomestic: boolean;

  packages: Package;

  segments: Segment[];

  constructor(res: any) {
    this.leg = res?.Leg || "";
    this.currency = res?.Currency || "";
    this.departure = res?.Departure || "";
    this.arrival = res?.Arrival || "";
    this.isRecommended = res?.IsRecommended || false;
    this.isSpecialDeal = res?.IsSpecialDeal || false;
    this.flightTotalTime = res?.FlightTotalTime || 0;
    this.flightTotalDuration = res?.FlightTotalDuration || "";
    this.flightTotalStop = res?.FlightTotalStop || 0;
    this.timeUnit = res?.TimeUnit || 0;
    this.isDomestic = res?.IsDomestic || false;
    this.packages = new Package(res?.Packages);

    this.segments = res?.Segments?.map(
      (item: Segment) => new Segment(item),
    ) || [new Segment({})];
  }
}
export class ResponseDataFareSelectedClass {
  typeOfFares: string;

  typeOfFlight: string;

  passengers: Passengers;

  priceLists: FlightPriceLists;

  departure: RouteFlightClass;

  return: RouteFlightClass;

  constructor(res: any) {
    this.typeOfFares = res?.typeOfFares || "";
    this.typeOfFlight = res?.typeOfFlight || "";
    this.passengers = new PassengersClass(res?.passengers);
    this.priceLists = new FlightPriceLists(res?.priceLists);
    this.departure = new RouteFlightClass(res?.departure);
    this.return = new RouteFlightClass(res?.return);
  }
}

export class ResponseFareSelectedPresenter extends ResponsePresenter<ResponseDataFareSelectedClass> {
  version: string;

  code: string;

  constructor(data: any) {
    super(data, (i) => new ResponseDataFareSelectedClass(i));
    this.version = data?.version || "";
    this.code = data?.code || "";
  }
}
